import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { Minus, Plus } from "lucide-react";

interface WorkforceIndividualEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workforce: {
    id?: string;
    name?: string;
    function?: string;
    quantity?: number;
    type?: string;
    employeeType?: string;
    team?: string;
    assignedTeam?: string;
    observations?: string;
  };
  onSave: (updatedWorkforce: any) => void;
}

export function WorkforceIndividualEditModal({
  open,
  onOpenChange,
  workforce,
  onSave
}: WorkforceIndividualEditModalProps) {
  const [quantity, setQuantity] = useState(workforce.quantity || 1);

  useEffect(() => {
    if (open) {
      setQuantity(workforce.quantity || 1);
    }
  }, [open, workforce.quantity]);

  const handleQuantityChange = (newQty: number) => {
    if (newQty >= 0) {
      setQuantity(newQty);
    }
  };

  const handleSave = () => {
    onSave({
      ...workforce,
      quantity
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    setQuantity(workforce.quantity || 1);
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>Editar Mão de Obra Individual</DialogTitle>
          <DialogDescription>
            Ajuste a quantidade para este colaborador ou profissão
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={workforce.name || "—"}
              disabled
              className="bg-muted"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="function">Profissão</Label>
            <Input
              id="function"
              value={workforce.function || "—"}
              disabled
              className="bg-muted"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="quantity">Quantidade</Label>
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={quantity <= 0}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                id="quantity"
                type="number"
                min="0"
                value={quantity}
                onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 0)}
                className="text-center w-20"
                autoFocus
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuantityChange(quantity + 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {quantity === 0 && (
              <p className="text-sm text-muted-foreground">
                Definir quantidade como 0 removerá este item ao salvar
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}